import React from 'react';
import jwt_decode from 'jwt-decode';
import useAuth from '../hooks/useAuth';
import {
  UserClient,
  AccountClient,
  TradeClient,
  BrokerClient,
  BillingClient,
  AnalyticsClient,
  NewsClient,
  NotificationClient,
  SentimentClient,
  StatusClient,
  AnalyzerServiceClient,
  FeatureRequestServiceClient,
  TenantClient,
  EAHostingClient,
  AchievementClient
} from './client';

const hasPermission = (token: string) => {
  const decoded: any = jwt_decode(token);
  if (decoded.permissions.length > 0) {
    return true;
  } else {
    return false;
  }
};

const tokenNotExpired = (token: string) => {
  if (token.length > 0) {
    const decoded: any = jwt_decode(token);
    const expireTime = new Date(decoded.exp * 1000);
    if (expireTime < new Date()) {
      return false;
    }
    return true;
  }
  return false;
};

type AuthorizedClient = { setMeta: (key: string, value: string) => void };

// One authorised getter per service. `refreshToken` is held in a ref so the getter changes only with the
// access token: effects depend on it, and a new getter per auth render refetched every page.
function useAuthorizedClient<T extends AuthorizedClient>(create: () => T, noStore = false): () => Promise<T> {
  const { accessToken, refreshToken } = useAuth();
  const client = React.useRef<T>();
  if (!client.current) client.current = create();
  const refresh = React.useRef(refreshToken);
  refresh.current = refreshToken;

  return React.useCallback(() => {
    const instance = client.current as T;
    const apply = (token: string) => {
      instance.setMeta('Authorization', `Bearer ${token}`);
      if (noStore) instance.setMeta('Cache-Control', 'no-store');
      return instance;
    };
    // jwt_decode throws on an empty or malformed token; guarded so the promise always settles.
    let usable = false;
    try {
      usable = !!accessToken && hasPermission(accessToken) && tokenNotExpired(accessToken);
    } catch {
      usable = false;
    }
    if (usable) return Promise.resolve(apply(accessToken as string));
    return refresh.current().then((token) => apply(token as string));
  }, [accessToken, noStore]);
}

export const useUserClient = (): (() => Promise<UserClient>) => useAuthorizedClient(() => new UserClient(), true);

export const useTenantClient = (): (() => Promise<TenantClient>) => useAuthorizedClient(() => new TenantClient(), true);

export const useAccountClient = (): (() => Promise<AccountClient>) => useAuthorizedClient(() => new AccountClient(), true);

export const useTradeClient = (): (() => Promise<TradeClient>) => useAuthorizedClient(() => new TradeClient());

export const useBrokerClient = (): (() => Promise<BrokerClient>) => useAuthorizedClient(() => new BrokerClient());

export const useBillingClient = (): (() => Promise<BillingClient>) => useAuthorizedClient(() => new BillingClient());

export const useAnalyticsClient = (): (() => Promise<AnalyticsClient>) => useAuthorizedClient(() => new AnalyticsClient());

export const useNewsClient = (): (() => Promise<NewsClient>) => useAuthorizedClient(() => new NewsClient());

export const useNotificationClient = (): (() => Promise<NotificationClient>) => useAuthorizedClient(() => new NotificationClient());

export const useSentimentClient = (): (() => Promise<SentimentClient>) => useAuthorizedClient(() => new SentimentClient());

export const useStatusClient = (): (() => Promise<StatusClient>) => useAuthorizedClient(() => new StatusClient());

export const useAnalyzerClient = (): (() => Promise<AnalyzerServiceClient>) => useAuthorizedClient(() => new AnalyzerServiceClient());

export const useFeatureRequestClient = (): (() => Promise<FeatureRequestServiceClient>) =>
  useAuthorizedClient(() => new FeatureRequestServiceClient(), true);

export const useEAHostingClient = (): (() => Promise<EAHostingClient>) => useAuthorizedClient(() => new EAHostingClient(), true);

export const useAchievementClient = (): (() => Promise<AchievementClient>) => useAuthorizedClient(() => new AchievementClient(), true);
